// "eCW claims report" — Setup > Data card (2026-10-20).
//
// Valetudo and Alleviate bill from eClinicalWorks and are fed through Cobalt, so the
// Data page offers them no upload box. Cobalt stops reading a claim after 90 days and
// misses the ones created late; eCW's own weekly claims report, uploaded here, refreshes
// the first and brings the second. The server converts, matches and writes
// (services/ecw_report_service.py); this posts the file and renders the counts. Nothing
// here names a patient: the response is counts and warnings.
//
//   GET  /api/practices/<id>/ecw-report   applies, running, last_run, fetch_wins, formats
//   POST /api/practices/<id>/ecw-report   multipart: file, full_ages, dry_run
//
// A real run is a background job on the server (a full-ages file can create a
// practice's whole history): the POST answers 202 {status: "running"} and this polls
// the GET every three seconds, for ten minutes at most, until `running` is false,
// then shows the run the GET carries (its counts, or its failure). A preview
// ("Preview only") answers 200 with its counts and changes nothing.

import React, { useCallback, useEffect, useState } from "react";

import { formatWhen } from "./DataPage";
import "./EcwReportCard.css";

const API_URL = process.env.REACT_APP_API_URL || "";

export const POLL_MS = 3000;
// Consecutive failed polls tolerated before the card gives up on the job.
export const POLL_FAILURES_TOLERATED = 3;
export const POLL_LIMIT_MS = 10 * 60 * 1000;
const STILL_RUNNING =
  "The import is still running after ten minutes; reload the page to see its result.";
const NO_RUN_RECORDED =
  "The import ended without recording a run; check the server log.";

export function ecwReportUrl(practiceId) {
  return `${API_URL}/api/practices/${practiceId}/ecw-report`;
}

function plural(n, one, many) {
  return `${n} ${Number(n) === 1 ? one : many}`;
}

// "12 claims: 3 created, 7 updated, 2 left to Cobalt (under 90 days); 4 open claims
// not in the report", with "; 1 row skipped" only when a row was, "; N left to the
// Cobalt fetch" only under the fetch_wins switch, and "; N ambiguous" only when two
// Atlas claims tied for a file claim. `counts` is the service's own block
// (ecw_report_service.COUNT_KEYS).
export function countsLine(counts) {
  if (!counts) return "";
  const n = (key) => Number(counts[key]) || 0;
  let line =
    `${plural(n("claims"), "claim", "claims")}: ${n("created")} created, ` +
    `${n("updated")} updated, ${n("cobalt_owned")} left to Cobalt (under 90 days); ` +
    `${plural(n("flagged_not_in_report"), "open claim", "open claims")} not in the report`;
  if (n("fetch_wins_skipped") > 0) {
    line += `; ${n("fetch_wins_skipped")} left to the Cobalt fetch`;
  }
  if (n("skipped_rows") > 0) {
    line += `; ${plural(n("skipped_rows"), "row", "rows")} skipped`;
  }
  if (n("ambiguous") > 0) {
    line += `; ${n("ambiguous")} ambiguous (matched to nothing, a person decides)`;
  }
  return line;
}

async function request(target, options, fallback) {
  const response = await fetch(target, { credentials: "include", ...options });
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    const err = new Error((body && body.error) || fallback);
    err.status = response.status;
    throw err;
  }
  if (!body || typeof body !== "object") {
    throw new Error("The response could not be read. Try again.");
  }
  return body;
}

function Message({ kind, text, testId, onClose }) {
  return (
    <div className={`data-message data-message-${kind}`} data-testid={testId}>
      <span>{text}</span>
      <button
        type="button"
        className="data-message-close"
        aria-label="Dismiss"
        onClick={onClose}
      >
        ×
      </button>
    </div>
  );
}

export default function EcwReportCard({ practiceId }) {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // The route is admins-only while the Data page is not, and the card is for a
  // practice billing from eCW only: for anyone else, or any other practice, the card is
  // not there rather than a red line on every visit.
  const [hidden, setHidden] = useState(false);
  const [file, setFile] = useState(null);
  const [fullAges, setFullAges] = useState(true);
  const [dryRun, setDryRun] = useState(false);
  const [busy, setBusy] = useState(false);
  // A job holds the practice's import guard on the server; while it does, the card
  // polls and the Upload button is off. `runBefore` is the run on record when this
  // card started the job: a poll that ends with the same run means the job ended
  // without recording one, and the old counts must not read as the new ones.
  const [running, setRunning] = useState(false);
  const [runBefore, setRunBefore] = useState(undefined);
  const [result, setResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    if (!practiceId) return Promise.resolve(null);
    return request(
      ecwReportUrl(practiceId),
      {},
      "Could not load the eCW report status.",
    );
  }, [practiceId]);

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    setData(null);
    setLoadError(null);
    setHidden(false);
    setFile(null);
    setFullAges(true);
    setDryRun(false);
    setRunning(false);
    setResult(null);
    setUploadError(null);
    setMessage(null);
    load()
      .then((body) => {
        if (cancelled || !body) return;
        if (body.applies !== true) {
          setHidden(true);
          return;
        }
        setData(body);
        if (body.running === true) setRunning(true);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err.status === 403) setHidden(true);
        else setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [practiceId, load]);

  // While a job runs, ask the server every three seconds whether it still does; when
  // it stops, the run it recorded is what the card shows. Bounded: after ten minutes
  // the card stops asking and says so.
  useEffect(() => {
    if (!running || !practiceId) return undefined;
    const startedAt = Date.now();
    let cancelled = false;
    let failures = 0;
    const tick = () =>
      load()
        .then((body) => {
          if (cancelled || !body) return;
          failures = 0;
          if (body.running === true) {
            if (Date.now() - startedAt >= POLL_LIMIT_MS) {
              setRunning(false);
              setUploadError(STILL_RUNNING);
            }
            return;
          }
          setRunning(false);
          if (body.applies === true) setData(body);
          const run = body.last_run;
          const stamp = run ? run.uploaded_at : null;
          if (runBefore !== undefined && stamp === runBefore) {
            setUploadError(NO_RUN_RECORDED);
          } else if (run && run.status === "failed") {
            setUploadError(run.error || "The report could not be imported.");
          } else if (run) {
            setMessage(`Report imported: ${countsLine(run.counts)}.`);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          // One failed poll is a blip while the job runs on; a run of them is not.
          failures += 1;
          if (failures < POLL_FAILURES_TOLERATED) return;
          setRunning(false);
          setUploadError(err.message);
        });
    const timer = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [running, practiceId, load, runBefore]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    setUploadError(null);
    setMessage(null);
    setResult(null);
    const body = new FormData();
    body.append("file", file);
    body.append("full_ages", fullAges ? "1" : "0");
    body.append("dry_run", dryRun ? "1" : "0");
    try {
      const payload = await request(
        ecwReportUrl(practiceId),
        { method: "POST", body },
        "The report could not be imported.",
      );
      setFile(null);
      if (payload.status === "running") {
        // The job's result is the run the GET carries; the poll below reads it.
        setRunBefore(data && data.last_run ? data.last_run.uploaded_at : null);
        setRunning(true);
        return;
      }
      setResult(payload);
      if (payload.dry_run) {
        setMessage(
          `Preview: ${countsLine(payload.counts)}. Nothing was changed.`,
        );
      } else {
        setMessage(`Report imported: ${countsLine(payload.counts)}.`);
        load()
          .then((fresh) => {
            if (fresh && fresh.applies === true) setData(fresh);
          })
          .catch(() => {
            /* the counts are on screen; the stamp refreshes on the next visit */
          });
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!practiceId || hidden) return null;
  if (!data && !loadError) return null;

  const lastRun = data && data.last_run;
  const warnings =
    (result && result.warnings) || (lastRun && lastRun.warnings) || [];
  const locked = busy || running;

  return (
    <div className="data-card ecw-report" data-testid="ecw-report">
      <h2>eCW claims report</h2>
      <p className="data-note">
        eCW&rsquo;s weekly claims report refreshes the claims Cobalt has stopped
        reading and brings the ones created late.
      </p>

      {loadError && <p className="data-error-line">{loadError}</p>}

      {data && (
        <>
          {lastRun ? (
            <p className="ecw-last" data-testid="ecw-last">
              Last upload <strong>{formatWhen(lastRun.uploaded_at)}</strong>
              {lastRun.status === "failed" ? (
                <>
                  {" "}
                  &middot; <span className="ecw-failed">failed</span>
                  {lastRun.error ? ` (${lastRun.error})` : ""}
                </>
              ) : (
                lastRun.counts && <> &middot; {countsLine(lastRun.counts)}</>
              )}
            </p>
          ) : (
            <p className="data-note" data-testid="ecw-empty">
              No report uploaded yet.
            </p>
          )}
          {data.fetch_wins && (
            <p className="data-note ecw-muted">
              Where the report and the Cobalt feed disagree, the feed wins.
            </p>
          )}
          {running && (
            <p className="data-note ecw-running" data-testid="ecw-running">
              Importing the report&hellip; this card checks every few seconds
              and shows the counts when it finishes.
            </p>
          )}

          {warnings.length > 0 && (
            <ul className="ecw-warnings" data-testid="ecw-warnings">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}

          <div className="ecw-upload">
            <label className="ecw-file">
              <span>
                {(data.formats && data.formats[0]) || "eCW Claims Report"}
              </span>
              <input
                type="file"
                accept=".csv,.xls,.xlsx,text/csv"
                data-testid="ecw-file"
                disabled={locked}
                onChange={(e) => {
                  const chosen = e.target.files[0];
                  if (chosen) setFile(chosen);
                  e.target.value = "";
                }}
              />
              {file && <span className="data-filename">{file.name}</span>}
            </label>
            <label className="ecw-switch">
              <input
                type="checkbox"
                data-testid="ecw-full-ages"
                checked={fullAges}
                disabled={locked}
                onChange={(e) => setFullAges(e.target.checked)}
              />
              Every age is in this report (flag open claims missing from it)
            </label>
            <label className="ecw-switch">
              <input
                type="checkbox"
                data-testid="ecw-dry-run"
                checked={dryRun}
                disabled={locked}
                onChange={(e) => setDryRun(e.target.checked)}
              />
              Preview only (count, change nothing)
            </label>
            <button
              type="button"
              className="data-button"
              data-testid="ecw-upload"
              disabled={locked || !file}
              onClick={upload}
            >
              {running
                ? "Importing…"
                : busy
                  ? "Uploading…"
                  : dryRun
                    ? "Preview"
                    : "Upload"}
            </button>
          </div>

          {uploadError && (
            <Message
              kind="error"
              text={uploadError}
              testId="ecw-upload-error"
              onClose={() => setUploadError(null)}
            />
          )}
          {message && (
            <Message
              kind="success"
              text={message}
              testId="ecw-upload-message"
              onClose={() => setMessage(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
